import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { getContent } from '../../lib/api';
import type { ContentBundle, Speaker } from '../../lib/types';
import { pick } from '../../lib/types';
import { useT } from '../../i18n/LangProvider';

type Cell = { kind: 'speaker'; sp: Speaker } | { kind: 'spacer'; key: string };

// Speakers with no Order set sort to the end. An empty admin field arrives as ''
// which would otherwise compare as 0 and jump the speaker to the front.
function rank(order: unknown): number {
  if (order === null || order === undefined || order === '') return Number.MAX_SAFE_INTEGER;
  const n = Number(order);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
}

// Desktop grid is 3 columns. A speaker marked RowBreak gets the rest of that
// row padded with invisible spacers so the next speaker starts a fresh row.
function layoutCells(speakers: Speaker[]): Cell[] {
  const ordered = [...speakers].sort((a, b) => rank(a.Order) - rank(b.Order));
  const cells: Cell[] = [];
  let col = 0;
  for (const sp of ordered) {
    cells.push({ kind: 'speaker', sp });
    col++;
    if (sp.RowBreak) {
      while (col % 3 !== 0) { cells.push({ kind: 'spacer', key: `spacer-${sp.id}-${col}` }); col++; }
    }
  }
  return cells;
}

export default function SpeakersPage() {
  const [content, setContent] = useState<ContentBundle | null>(null);
  const { lang } = useT();
  useEffect(() => { getContent().then(setContent).catch(() => {}); }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-20 pt-36 pb-32 grid grid-cols-12 gap-6">
      <div className="col-span-12 md:col-span-10 md:col-start-2">
        <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-xs tracking-[0.25em] uppercase text-brand-red mb-5 font-mono">{lang === 'fr' ? 'Sur scène' : 'The people on stage'}</motion.p>
        <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="font-display text-5xl md:text-7xl tracking-tight mb-16 text-ink">{lang === 'fr' ? 'Conférenciers' : 'Speakers'}</motion.h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {layoutCells(content?.speakers ?? []).map((cell, i) => {
            if (cell.kind === 'spacer') return <div key={cell.key} className="hidden lg:block" aria-hidden="true" />;
            const sp = cell.sp;
            const name = pick(lang, sp.TitleEn, sp.TitleFr, sp.Title);
            const job = pick(lang, sp.JobTitleEn, sp.JobTitleFr, sp.JobTitle);
            const bio = pick(lang, sp.BioEn, sp.BioFr, sp.Bio);
            return (
              <motion.article key={sp.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.5, delay: Math.min(i * 0.05, 0.4) }} className="group">
                <div className="aspect-square rounded-2xl overflow-hidden bg-bg-elev border border-white/[0.07]">
                  {sp.PhotoUrl ? (
                    <img src={sp.PhotoUrl} alt={name} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center font-display text-5xl text-ink-faint">{name?.[0]}</div>
                  )}
                </div>
                <h3 className="font-display text-xl mt-4 tracking-tight text-ink">{name}</h3>
                <p className="text-sm text-ink-muted mt-0.5">{job}{sp.Company ? ` · ${sp.Company}` : ''}</p>
                {bio && <p className="text-sm text-ink-muted/80 mt-3 leading-relaxed">{bio}</p>}
              </motion.article>
            );
          })}
          {(content?.speakers?.length ?? 0) === 0 && <p className="text-ink-muted col-span-full">{lang === 'fr' ? 'Intervenants bientôt annoncés.' : 'Speakers announced soon.'}</p>}
        </div>
      </div>
    </div>
  );
}
